import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  ImageBbox,
  InventoryItemResponse,
} from '../../api/types';

/**
 * Segmentation-in-flight state. Lives OUTSIDE `browseSlice` so the
 * live scan surface doesn't pollute the Items grid cache (and vice
 * versa). When the user confirms, Chunk 5 will forward the accepted
 * items into `browseSlice` via `upsertInventoryItem`; rejected items
 * are soft-deleted server-side + purged from the client.
 *
 * Keys:
 *   - items: Record<id, InventoryItemResponse> — keyed for O(1)
 *     merge when a `mask` event lands that must patch an existing
 *     `item` event's id.
 *   - itemOrder: number[] — separate ordering slot so rail renders
 *     in the sequence items arrived (newest first via `unshift`).
 *   - masks: Record<id, MaskRecord> — keyed by inventory_item_id.
 *     Backend bbox + mask_url are frozen server-side once written,
 *     so we never merge; we replace.
 *   - maskErrors: Record<id, …> — for the dashed-rim "Annotate"
 *     CTA per PRD §4 mask_error handling.
 *   - selected: Set<number> — accepted set. Starts as "all items
 *     with a mask"; user ticks off (✕) to reject.
 *   - timings: perf marks for the scorecard (Chunk 5).
 *   - events: last-40 SSE event ring buffer for the
 *     <details> disclosure on the stage (PRD §8.12).
 *   - pendingReviews: persisted resume-banner queue for the user-
 *     aborted scan case (PRD §12 + design-agent recommendation).
 */

export interface MaskRecord {
  inventory_item_id: number;
  bbox: ImageBbox;
  /** NULL on the Gemini native-bbox path. Mask layer skips render;
   *  bbox layer still draws the rectangle. */
  mask_url: string | null;
  confidence: number;
  model:
    | 'sam3'
    | 'user_box'
    | 'user_box_fallback'
    | 'gemini_native_bbox'
    | string;
  label_used?: string;
  prompt_text_used?: string;
  candidate_mask_count?: number;
  /** Set by the resegment thunk in Chunk 3. */
  iou_original_vs_new?: number;
  area_ratio_new_over_original?: number;
  centroid_shift_px?: number;
  /** Sentinel flag for the IoU pill badge. */
  _corrected?: boolean;
}

export interface MaskErrorRecord {
  inventory_item_id: number;
  label_used?: string;
  error: string;
}

export interface EventLogEntry {
  ts_ms: number;
  source: 'scan' | 'primary-photo' | 'rest';
  event: string;
  ok: boolean;
  data?: unknown;
}

export interface PendingReview {
  file_record_id: number;
  unconfirmed_ids: number[];
  created_at: string;
}

export type SegmentStatus =
  | 'idle'
  | 'scanning'       // upload in flight, waiting for first `item`
  | 'streaming'      // at least one item received
  | 'segmenting'     // all items detected; masks still arriving
  | 'complete'       // `complete` event received
  | 'confirming'     // POST /inventory/confirm/batch in flight
  | 'done'           // confirm succeeded; primary-photo grid in view
  | 'error';

export type PrimaryPhotoState =
  | 'idle'           // no stream opened yet
  | 'waiting'        // server still polling fal.ai
  | 'ready'          // primary_photo_ready landed
  | 'fallback'       // fell back to room photo / crop_only
  | 'timeout'        // stream gave up (90s default)
  | 'error';

export interface PrimaryPhotoRecord {
  inventory_item_id: number;
  state: PrimaryPhotoState;
  /** Final URL when state === 'ready' or 'fallback' (room photo). */
  url?: string | null;
  /** crop_upscale | crop_only | original — explains the URL choice. */
  source?: 'crop_upscale' | 'crop_only' | 'original' | null;
  /** Last 'waiting' ping's elapsed_seconds — surfaces a progress hint. */
  elapsed_seconds?: number;
  error?: string;
}

export interface SegmentState {
  file_record_id: number | null;
  storage_url: string | null;
  image_width_px: number | null;
  image_height_px: number | null;

  items: Record<number, InventoryItemResponse>;
  itemOrder: number[];

  masks: Record<number, MaskRecord>;
  maskErrors: Record<number, MaskErrorRecord>;

  ai_description: string | null;
  room_context: string | null;

  status: SegmentStatus;
  error: string | null;

  selected: Record<number, true>; // Set-like; plain record for Redux-immer friendliness

  /** Set when the user enters refine mode (Chunk 3). */
  editing_item_id: number | null;

  /** Confirm-batch result. After a successful POST
   *  /inventory/confirm/batch, this lists the items the server
   *  accepted (which kicked off primary-photo jobs) and the ones
   *  it rejected. The done view reads accepted_ids; rejected
   *  ids are dropped from `items` already by the reducer.   */
  confirmedAcceptedIds: number[];
  confirmedRejectedIds: number[];

  /** Per-item primary-photo state, keyed by inventory_item_id.
   *  Updated by the SSE stream events from
   *  GET /inventory/{id}/primary-photo/stream. Reads on the done
   *  view drive the per-card skeleton / image / fallback states. */
  primaryPhotos: Record<number, PrimaryPhotoRecord>;

  timings: {
    t_start?: number;
    t_first_visual?: number;
    t_first_item?: number;
    t_first_mask?: number;
    t_last_mask?: number;
    t_complete?: number;
  };

  events: EventLogEntry[];

  pendingReviews: PendingReview[];
}

const INITIAL: SegmentState = {
  file_record_id: null,
  storage_url: null,
  image_width_px: null,
  image_height_px: null,

  items: {},
  itemOrder: [],

  masks: {},
  maskErrors: {},

  ai_description: null,
  room_context: null,

  status: 'idle',
  error: null,

  selected: {},
  editing_item_id: null,

  confirmedAcceptedIds: [],
  confirmedRejectedIds: [],
  primaryPhotos: {},

  timings: {},

  events: [],

  pendingReviews: [],
};

const MAX_EVENTS = 40;

const segmentSlice = createSlice({
  name: 'segment',
  initialState: INITIAL,
  reducers: {
    /** FE #124.7 — Hydrate state from a backend ScanDetailResponse
     *  fetched via GET /users/scans/{file_record_id}.
     *
     *  Used by SegmentScreen's resume flow: when the user navigates
     *  to /scan?resume=true&file_record_id=N, we fetch the detail,
     *  seed Redux with the items + bboxes the server already has,
     *  then either re-attach the SSE stream (IN_PROGRESS) OR show
     *  the existing items for review (READY_TO_SAVE).
     *
     *  Behaviour:
     *   - Resets to INITIAL then writes the seed (so a stale prior
     *     session doesn't leak through).
     *   - Seeds `items`, `itemOrder` from the detail's `items[]`.
     *   - Seeds `masks` from each item's `bbox` (treating the bbox
     *     as the polygon — backend's bbox-only pipeline doesn't ship
     *     pixel masks for non-segmented items).
     *   - Auto-selects every item with a bbox (matches the live
     *     stream's behaviour in `maskStreamed`).
     *   - Status set to `complete` for READY_TO_SAVE (lets the Save
     *     CTA enable immediately) or `streaming` for IN_PROGRESS
     *     (we expect more events from the resume SSE).
     */
    seedFromBackend(
      state,
      action: PayloadAction<{
        file_record_id: number;
        storage_url: string | null;
        ai_description: string | null;
        room_context: string | null;
        items: InventoryItemResponse[];
        status: 'IN_PROGRESS' | 'READY_TO_SAVE' | 'FAILED' | 'CANCELLED';
      }>,
    ) {
      const {
        file_record_id,
        storage_url,
        ai_description,
        room_context,
        items,
        status,
      } = action.payload;
      const preserved = state.pendingReviews;
      Object.assign(state, INITIAL);
      state.pendingReviews = preserved;
      state.file_record_id = file_record_id;
      state.storage_url = storage_url;
      state.ai_description = ai_description;
      state.room_context = room_context;
      for (const item of items) {
        if (typeof item.id !== 'number') continue;
        state.items[item.id] = item;
        state.itemOrder.push(item.id);
        const bbox = (item as any).bbox as
          | { x: number; y: number; w: number; h: number }
          | undefined;
        if (bbox) {
          // Backend's bbox-only pipeline doesn't emit pixel masks;
          // we synthesize a MaskRecord pointing at the bbox so the
          // annotation stage can draw a selection rectangle.
          state.masks[item.id] = {
            inventory_item_id: item.id,
            bbox,
            mask_url: (item as any).mask_url ?? null,
            confidence: (item as any).segmentation_confidence ?? 1,
            model:
              (item as any).segmentation_model ?? 'gemini_native_bbox',
          };
          state.selected[item.id] = true;
        }
      }
      // IN_PROGRESS leaves status as 'streaming' so the resume SSE
      // consumer can append more items + flip to 'complete' on the
      // terminal event. READY_TO_SAVE / FAILED / CANCELLED jump
      // straight to 'complete' so the Save CTA renders.
      state.status = status === 'IN_PROGRESS' ? 'streaming' : 'complete';
    },

    /** User picked an image; scan hasn't started yet. Flips us out of
     *  idle so the scanning hero can render immediately (TTFV gate). */
    scanStarted(state, action: PayloadAction<{ t_start: number }>) {
      // Preserve pendingReviews + empty everything else.
      const preserved = state.pendingReviews;
      Object.assign(state, INITIAL);
      state.pendingReviews = preserved;
      state.status = 'scanning';
      state.timings.t_start = action.payload.t_start;
      state.timings.t_first_visual = action.payload.t_start;
    },

    /** First SSE event — backend acknowledged our upload. Stash the
     *  file_record_id for every subsequent call in this session. */
    scanStatus(state, action: PayloadAction<{ file_record_id: number }>) {
      state.file_record_id = action.payload.file_record_id;
    },

    /** `segmentation_started` — cache natural image dimensions so
     *  the transform hook has an authoritative source (bbox coords
     *  elsewhere are in this space). */
    segmentationStarted(
      state,
      action: PayloadAction<{
        image_width_px: number;
        image_height_px: number;
      }>,
    ) {
      state.image_width_px = action.payload.image_width_px;
      state.image_height_px = action.payload.image_height_px;
    },

    /** Per-item `item` SSE event. Prepend to `itemOrder` — newest
     *  first — and flip the status out of `scanning` on the first
     *  arrival. */
    itemStreamed(
      state,
      action: PayloadAction<{ item: InventoryItemResponse; t: number }>,
    ) {
      const { item, t } = action.payload;
      if (typeof item.id !== 'number') return;
      const isNew = !state.items[item.id];
      state.items[item.id] = item;
      if (isNew) {
        state.itemOrder.unshift(item.id);
        if (state.status === 'scanning' || state.status === 'idle') {
          state.status = 'streaming';
        }
        if (state.timings.t_first_item === undefined) {
          state.timings.t_first_item = t;
        }
      }
    },

    /** Per-item `mask` SSE event. Replace (not merge) — backend
     *  writes these atomically. Auto-select the item on first mask
     *  so the user gets sensible defaults for confirm-batch later. */
    maskStreamed(
      state,
      action: PayloadAction<{ mask: MaskRecord; t: number }>,
    ) {
      const { mask, t } = action.payload;
      const wasCorrected = state.masks[mask.inventory_item_id]?._corrected;
      state.masks[mask.inventory_item_id] = {
        ...mask,
        // Once corrected, always corrected: the refine handler
        // dispatches with `_corrected: true`, AND a backend stream
        // re-emit for the same item must not silently clear the flag.
        _corrected: mask._corrected || wasCorrected,
      };
      if (state.maskErrors[mask.inventory_item_id]) {
        delete state.maskErrors[mask.inventory_item_id];
      }
      // Auto-select on first mask arrival.
      if (!state.selected[mask.inventory_item_id]) {
        state.selected[mask.inventory_item_id] = true;
      }
      if (state.timings.t_first_mask === undefined) {
        state.timings.t_first_mask = t;
      }
      state.timings.t_last_mask = t;
    },

    /** `mask_error` — per-item SAM3 failure. Don't select; surface
     *  via dashed amber rim + Annotate CTA. */
    maskErrored(state, action: PayloadAction<MaskErrorRecord>) {
      state.maskErrors[action.payload.inventory_item_id] = action.payload;
    },

    /** `summary` — stash for the context card and Done view. */
    summaryReceived(
      state,
      action: PayloadAction<{ ai_description: string; room_context: string }>,
    ) {
      state.ai_description = action.payload.ai_description;
      state.room_context = action.payload.room_context;
    },

    /** `segmentation_complete` — all SAM3 calls settled. Transition
     *  status so the Save CTA can enable. */
    segmentationComplete(state) {
      if (state.status === 'streaming') state.status = 'segmenting';
    },

    /** `complete` — terminal scan event. Record perf mark. */
    streamCompleted(state, action: PayloadAction<{ t: number }>) {
      state.status = 'complete';
      state.timings.t_complete = action.payload.t;
    },

    /** `error` — fatal mid-stream failure. Keep items already
     *  streamed per PRD §4 partial-failure. */
    streamErrored(
      state,
      action: PayloadAction<{ error: string; items_persisted?: number }>,
    ) {
      state.error = action.payload.error;
      // Keep items already delivered; only flip status if nothing
      // survived the failure.
      if (!action.payload.items_persisted) state.status = 'error';
      else state.status = 'complete';
    },

    /** Optimistic transition: user tapped Save N. Status flips to
     *  `confirming` so the UI can show a loading state on the
     *  CTA. The actual confirmation network call happens outside
     *  the slice; on success the parent dispatches confirmCompleted. */
    confirmStarted(state) {
      if (state.status === 'complete') state.status = 'confirming';
    },

    /** POST /inventory/confirm/batch landed. Records the accepted
     *  + rejected sets and seeds an `idle` primary-photo record
     *  per accepted item so the done view can render skeletons
     *  before the SSE streams begin. Rejected items stay in the
     *  client store but the done view filters to accepted_ids. */
    confirmCompleted(
      state,
      action: PayloadAction<{
        accepted_ids: number[];
        rejected_ids: number[];
      }>,
    ) {
      state.status = 'done';
      state.confirmedAcceptedIds = action.payload.accepted_ids;
      state.confirmedRejectedIds = action.payload.rejected_ids;
      for (const id of action.payload.accepted_ids) {
        if (!state.primaryPhotos[id]) {
          state.primaryPhotos[id] = {
            inventory_item_id: id,
            state: 'idle',
          };
        }
      }
    },

    /** Confirm-batch network failed. Roll status back to `complete`
     *  so the user sees the review surface again with a toast. */
    confirmFailed(state, action: PayloadAction<{ error: string }>) {
      if (state.status === 'confirming') state.status = 'complete';
      state.error = action.payload.error;
    },

    /** SSE `waiting` event from /inventory/{id}/primary-photo/stream.
     *  Surfaces a "still processing" hint with elapsed seconds. */
    primaryPhotoWaiting(
      state,
      action: PayloadAction<{
        inventory_item_id: number;
        elapsed_seconds: number;
      }>,
    ) {
      const { inventory_item_id, elapsed_seconds } = action.payload;
      const prev = state.primaryPhotos[inventory_item_id];
      state.primaryPhotos[inventory_item_id] = {
        inventory_item_id,
        state: prev?.state === 'ready' ? 'ready' : 'waiting',
        elapsed_seconds,
        url: prev?.url,
        source: prev?.source,
      };
    },

    /** SSE `primary_photo_ready` — happy path: a crop+upscale URL
     *  landed. Item's `primary_photo_url` is also patched on the
     *  underlying InventoryItemResponse so any future fetch sees it. */
    primaryPhotoReady(
      state,
      action: PayloadAction<{
        inventory_item_id: number;
        primary_photo_url: string;
        primary_photo_source: 'crop_upscale' | 'crop_only' | 'original';
        elapsed_seconds: number;
      }>,
    ) {
      const {
        inventory_item_id,
        primary_photo_url,
        primary_photo_source,
        elapsed_seconds,
      } = action.payload;
      state.primaryPhotos[inventory_item_id] = {
        inventory_item_id,
        state: 'ready',
        url: primary_photo_url,
        source: primary_photo_source,
        elapsed_seconds,
      };
      const item = state.items[inventory_item_id];
      if (item) {
        item.primary_photo_url = primary_photo_url;
        item.primary_photo_source = primary_photo_source;
      }
    },

    /** SSE `primary_photo_fallback` — upscale failed; backend
     *  returned the original room photo or a crop-only frame.
     *  Still a usable image; just less polished. */
    primaryPhotoFallback(
      state,
      action: PayloadAction<{
        inventory_item_id: number;
        primary_photo_source: 'crop_upscale' | 'crop_only' | 'original';
        elapsed_seconds: number;
      }>,
    ) {
      const { inventory_item_id, primary_photo_source, elapsed_seconds } =
        action.payload;
      const prev = state.primaryPhotos[inventory_item_id];
      state.primaryPhotos[inventory_item_id] = {
        inventory_item_id,
        state: 'fallback',
        url: prev?.url ?? null,
        source: primary_photo_source,
        elapsed_seconds,
      };
    },

    /** SSE `primary_photo_timeout` — server gave up after 90 s.
     *  Done view falls back to the original room photo (storage_url). */
    primaryPhotoTimeout(
      state,
      action: PayloadAction<{
        inventory_item_id: number;
        elapsed_seconds: number;
        message?: string;
      }>,
    ) {
      const { inventory_item_id, elapsed_seconds, message } = action.payload;
      state.primaryPhotos[inventory_item_id] = {
        inventory_item_id,
        state: 'timeout',
        elapsed_seconds,
        error: message,
      };
    },

    /** Stream-level error (transport layer). Reflects on the per-
     *  item card; user can retry by hitting the regenerate action
     *  in a future polish pass. */
    primaryPhotoErrored(
      state,
      action: PayloadAction<{
        inventory_item_id: number;
        error: string;
      }>,
    ) {
      const { inventory_item_id, error } = action.payload;
      state.primaryPhotos[inventory_item_id] = {
        inventory_item_id,
        state: 'error',
        error,
      };
    },

    /** Hard-remove an item from every sub-collection. Called after
     *  a successful DELETE /inventory/{id} in the review surface
     *  (Chunk 4). The server writes an `annotation_feedback` row
     *  before soft-deleting — we drop from the live client store so
     *  the item disappears from the stage + rail. */
    itemRemoved(state, action: PayloadAction<number>) {
      const id = action.payload;
      delete state.items[id];
      state.itemOrder = state.itemOrder.filter((x) => x !== id);
      delete state.masks[id];
      delete state.maskErrors[id];
      delete state.selected[id];
      if (state.editing_item_id === id) state.editing_item_id = null;
    },

    /** Toggle a single item's presence in `selected`. Used by the
     *  ✕ button on the bbox controls. */
    toggleSelected(state, action: PayloadAction<number>) {
      const id = action.payload;
      if (state.selected[id]) delete state.selected[id];
      else state.selected[id] = true;
    },

    /** Chunk 3 will dispatch these from the refine UI. */
    enterRefine(state, action: PayloadAction<number>) {
      state.editing_item_id = action.payload;
    },
    exitRefine(state) {
      state.editing_item_id = null;
    },

    /** Debug ring-buffer. Called from the stream consumer on every
     *  SSE event. Never more than 40 entries. */
    logEvent(state, action: PayloadAction<EventLogEntry>) {
      state.events.push(action.payload);
      if (state.events.length > MAX_EVENTS) {
        state.events.splice(0, state.events.length - MAX_EVENTS);
      }
    },

    /** Reset everything but `pendingReviews`. Called when the user
     *  bails out of the review surface and starts a new scan. */
    resetSession(state) {
      const preserved = state.pendingReviews;
      Object.assign(state, INITIAL);
      state.pendingReviews = preserved;
    },

    /** Persist an unconfirmed session so the re-entry banner can
     *  surface it on next app open. Chunk 5 writes through this. */
    pendingReviewAdded(state, action: PayloadAction<PendingReview>) {
      // Dedupe by file_record_id — same scan should only appear once.
      state.pendingReviews = state.pendingReviews.filter(
        (pr) => pr.file_record_id !== action.payload.file_record_id,
      );
      state.pendingReviews.push(action.payload);
    },
    pendingReviewCleared(state, action: PayloadAction<number>) {
      state.pendingReviews = state.pendingReviews.filter(
        (pr) => pr.file_record_id !== action.payload,
      );
    },
    pendingReviewsHydrated(state, action: PayloadAction<PendingReview[]>) {
      state.pendingReviews = action.payload;
    },
  },
});

export const {
  seedFromBackend,
  scanStarted,
  scanStatus,
  segmentationStarted,
  itemStreamed,
  maskStreamed,
  maskErrored,
  summaryReceived,
  segmentationComplete,
  streamCompleted,
  streamErrored,
  itemRemoved,
  toggleSelected,
  confirmStarted,
  confirmCompleted,
  confirmFailed,
  primaryPhotoWaiting,
  primaryPhotoReady,
  primaryPhotoFallback,
  primaryPhotoTimeout,
  primaryPhotoErrored,
  enterRefine,
  exitRefine,
  logEvent,
  resetSession,
  pendingReviewAdded,
  pendingReviewCleared,
  pendingReviewsHydrated,
} = segmentSlice.actions;

export default segmentSlice.reducer;

/* ─────────────────────────────────────────────────────────────── */
/* Selectors                                                       */
/* ─────────────────────────────────────────────────────────────── */

import type { RootState } from '../../store/store';

export const selectSegment = (state: RootState) => state.segment;
export const selectSegmentStatus = (state: RootState) =>
  state.segment.status;
export const selectSegmentError = (state: RootState) => state.segment.error;
export const selectScanItems = (state: RootState): InventoryItemResponse[] =>
  state.segment.itemOrder
    .map((id) => state.segment.items[id])
    .filter((it): it is InventoryItemResponse => !!it);
export const selectMasks = (state: RootState) => state.segment.masks;
export const selectMaskErrors = (state: RootState) =>
  state.segment.maskErrors;
export const selectSelectedSet = (state: RootState): Set<number> => {
  return new Set(
    Object.keys(state.segment.selected).map((k) => Number(k)),
  );
};
export const selectEventLog = (state: RootState) => state.segment.events;
export const selectSceneContext = (state: RootState) => ({
  ai_description: state.segment.ai_description,
  room_context: state.segment.room_context,
  image_width_px: state.segment.image_width_px,
  image_height_px: state.segment.image_height_px,
  storage_url: state.segment.storage_url,
  file_record_id: state.segment.file_record_id,
});
export const selectPendingReviews = (state: RootState) =>
  state.segment.pendingReviews;

export const selectConfirmedAcceptedIds = (state: RootState) =>
  state.segment.confirmedAcceptedIds;
export const selectPrimaryPhotos = (state: RootState) =>
  state.segment.primaryPhotos;
